import styles from '@hts/entities/Card/styles/InfoContent/TextFields/TextFields.module.scss';
import { calculateFontSizeFit } from '@hts/entities/Card/utils';
import { ELanguageType } from '@hts/shared/enums';
import { getFontSizeNumber, getTextLanguageByText } from '@hts/shared/utils';
import { AnimatePresence, motion, Transition, useReducedMotion } from 'framer-motion';
import { memo, useCallback, useMemo, useState } from 'react';
import { useCardContext, useToogleKeyboardView } from '../../../libs';
import { IKeyboardPosState, INewCardState, INewFieldItem } from '../../../types';
import { CardKeyboard } from './CardKeyboard';
import { TextField } from './TextField';

interface ITextFieldsProps {
  editAllFields: boolean;
}

const SHIFT_BY_Y = 200;
const SHIFT_BY_X = 200;

export const TextFields: React.FC<ITextFieldsProps> = memo(({ editAllFields }) => {
  const card = useCardContext((state) => state.card);
  const showKeyboard = useCardContext((state) => state.showKeyboard);
  const activeTextFieldId = useCardContext((state) => state.activeTextFieldId);
  const setCard = useCardContext((state) => state.setCard);
  const setActiveTextFieldId = useCardContext((state) => state.setActiveTextFieldId);
  const setShowKeyboard = useCardContext((state) => state.setShowKeyboard);

  const reduceMotion = useReducedMotion();

  const [keyboardLang, setKeyboardLang] = useState<ELanguageType>(ELanguageType.HEBREW);
  const [keyboardPos, setKeyboardPos] = useState<IKeyboardPosState>({
    top: 0,
    left: 0,
    width: 300,
  });

  const filteredFields = useMemo(
    () => card?.fields.filter(({ isHidden }) => editAllFields || !isHidden) || [],
    [card?.fields, editAllFields]
  );

  const animationTransition = useMemo<Transition>(
    () =>
      reduceMotion
        ? { duration: 0 }
        : { duration: 0.45, type: 'spring', stiffness: 150, damping: 18, mass: 0.5 },
    [reduceMotion]
  );

  const { keyboard, toggleKeyboardView } = useToogleKeyboardView({
    showKeyboard,
    setActiveKeyboardTextFieldId: setActiveTextFieldId,
    setKeyboardLang,
    setKeyboardPos,
    setShowKeyboard,
  });

  const onChangeKeyboardLang = useCallback((lang: string) => {
    setKeyboardLang(lang as ELanguageType);
  }, []);

  const handleChangeText = useCallback(
    (id: string, newText: string, originalInput?: boolean) => {
      if (!card) return;

      const newCard: INewCardState = {
        ...card,
        fields: card.fields.map((field) => {
          if (field.id !== id) return field;

          const newTextLang = getTextLanguageByText(newText);
          const direction = newTextLang === ELanguageType.HEBREW ? 'rtl' : 'ltr';

          const newFontSize = calculateFontSizeFit({
            text: newText,
            containerWidth: field.blockWidth,
            containerHeight: field.blockHeight,
            originalFontSize: getFontSizeNumber(field.font_size),
            changedFontSize: field.changedFontSize,
            fontFamily: field.font?.name,
            direction,
          });

          return {
            ...field,
            text: newText,
            text_lang: newTextLang,
            changedFontSize: newFontSize || undefined,
          };
        }),
      };

      if (originalInput) {
        keyboard.current.setInput(newText);
      }

      sessionStorage.setItem(newCard.cardId, JSON.stringify(newCard));

      setCard(newCard);
    },
    [card]
  );

  const handleChangeTextCardKeyboard = useCallback(
    (id: string, newText: string) => {
      handleChangeText(id, newText, false);
    },
    [handleChangeText]
  );

  const onFocus = useCallback(
    (id: string) => {
      if (activeTextFieldId && activeTextFieldId !== id && !showKeyboard) {
        setShowKeyboard(false);
      }

      const currentInput = card?.fields.find((field) => field.id === id);

      if (currentInput) {
        keyboard.current.setInput(currentInput.text);
      }

      setActiveTextFieldId(id);
    },
    [activeTextFieldId, card?.fields, showKeyboard]
  );

  const onBlur = useCallback(
    (id: string) => {
      if (showKeyboard && activeTextFieldId === id) return;

      setActiveTextFieldId(null);
      setShowKeyboard(false);
    },
    [activeTextFieldId, showKeyboard]
  );

  const onClickReset = useCallback(
    (id: string) => {
      if (!card) return;

      onBlur(id);

      let defaultText = '';

      const newCard: INewCardState = {
        ...card,
        fields: card.fields.map((field) => {
          if (field.id !== id) return field;

          defaultText = field.defaultText;

          return {
            ...field,
            text: field.defaultText,
            text_lang: field.initial_text_lang,
            changedFontSize: undefined,
          };
        }),
      };

      keyboard.current.setInput(defaultText);
      sessionStorage.setItem(newCard.cardId, JSON.stringify(newCard));

      setCard(newCard);
    },
    [card, onBlur]
  );

  const onClickBackspace = useCallback(
    (id: string) => {
      if (!card) return;

      onBlur(id);

      const newText = '';

      const newCard: INewCardState = {
        ...card,
        fields: card.fields.map((field) => {
          if (field.id !== id) return field;

          return { ...field, text: newText, text_lang: getTextLanguageByText(newText) };
        }),
      };

      keyboard.current.setInput(newText);
      sessionStorage.setItem(newCard.cardId, JSON.stringify(newCard));

      setCard(newCard);
    },
    [card, onBlur]
  );

  const renderTextField = useCallback(
    (item: INewFieldItem) => {
      if (item.type === 'signature') return null;

      const fromTop = item.isHidden && editAllFields;
      const fromBottom = item.isHidden && !editAllFields;

      return (
        <motion.li
          key={item.id}
          layout={!reduceMotion}
          initial={{
            opacity: 0,
            visibility: 'hidden',
            y: fromTop ? -SHIFT_BY_Y : fromBottom ? SHIFT_BY_Y : 0,
            x: fromTop || fromBottom ? -SHIFT_BY_X : 0,
            scale: fromTop || fromBottom ? 0.45 : 1,
          }}
          animate={{
            visibility: !editAllFields && item.isHidden ? 'hidden' : 'visible',
            opacity: 1,
            y: 0,
            x: 0,
            scale: 1,
          }}
          exit={{
            visibility: 'hidden',
            opacity: 0,
            y: fromTop ? -SHIFT_BY_Y : fromBottom ? SHIFT_BY_Y : 0,
            x: fromTop || fromBottom ? -SHIFT_BY_X : 0,
            scale: fromTop || fromBottom ? 0.45 : 1,
          }}
          style={{ willChange: 'transform, opacity' }}
          transition={animationTransition}
        >
          <TextField
            item={item}
            focused={activeTextFieldId === item.id}
            onBlur={onBlur}
            onChangeText={handleChangeText}
            onFocus={onFocus}
            onClickReset={onClickReset}
            onClickBackspace={onClickBackspace}
            toggleKeyboardView={toggleKeyboardView}
          />
        </motion.li>
      );
    },
    [
      editAllFields,
      activeTextFieldId,
      onBlur,
      handleChangeText,
      onFocus,
      onClickReset,
      onClickBackspace,
      toggleKeyboardView,
    ]
  );

  return (
    <>
      <ul className={styles.container}>
        <AnimatePresence mode="popLayout">{filteredFields.map(renderTextField)}</AnimatePresence>
      </ul>

      <CardKeyboard
        ref={keyboard}
        showKeyboard={showKeyboard}
        activeKeyboardTextFieldId={activeTextFieldId}
        keyboardLang={keyboardLang}
        keyboardPos={keyboardPos}
        setKeyboardLang={onChangeKeyboardLang}
        handleChangeText={handleChangeTextCardKeyboard}
      />
    </>
  );
});
